/**
 * @file PlanTimeInput.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-15
 * @description Every planning time field — the boards' collection times, the
 *              bulk board's drop times and every stop's time in the Lorry day
 *              window — behaves the same way (utils/planningTime):
 *                - focusing it FADES the time it holds (Cam, 2026-10-15:
 *                  "rather than clicking a time clearing it, just fade the
 *                  existing time out and allow overtyping of it on type");
 *                  typing replaces it, leaving without typing keeps it,
 *                  Escape puts it back;
 *                - leaving it saves a valid time — "9" → 09:00, "1012" →
 *                  10:12, "10.5" → 10:30, "10.12" → 10:12, "2pm" → 14:00;
 *                - Space, ".", Delete or Backspace with nothing typed, then
 *                  leaving it, clears the time;
 *                - something that is not a time (25:00, 10:75, "abc") is not
 *                  saved: the field shows what was typed, outlined, with the
 *                  reason as its title, until it is fixed or Escape is pressed.
 *              Enter commits. With `navRow` / `navCol` (inside a
 *              data-time-grid) the arrow keys move between time fields
 *              (utils/timeFieldNav).
 */

import React, { useEffect, useRef, useState } from 'react';
import { CLEAR_TIME_KEYS, timeEditOutcome } from '../utils/planningTime';
import { timeFieldKeyNav } from '../utils/timeFieldNav';
import './PlanTimeInput.css';

const shownTime = (v) => (v ? String(v).slice(0, 5) : '');

/**
 * @param {object}   props
 * @param {string}   props.value      'HH:MM' (or 'HH:MM:SS') or '' / null
 * @param {Function} props.onCommit   (time 'HH:MM' | null) → void, only when it changes
 */
function PlanTimeInput({ value, onCommit, disabled = false, title, className = 'time-input', ariaLabel, placeholder = 'HH:MM', id, navRow, navCol }) {
  const [editing, setEditing] = useState(null);
  // What was typed that is not a time: { typed, message }.
  const [problem, setProblem] = useState(null);
  const typedRef = useRef(null);
  const cleared = useRef(false);
  const escaped = useRef(false);
  const current = shownTime(value);

  // A new value from outside settles a problem left in the field.
  useEffect(() => { setProblem(null); }, [value]);

  const setTyped = (v) => { typedRef.current = v; setEditing(v); };
  const shown = editing !== null ? editing : (problem ? problem.typed : current);
  const showProblem = !!problem && editing === null;

  const commit = () => {
    const typed = typedRef.current;
    setTyped(null);
    if (escaped.current) { escaped.current = false; cleared.current = false; setProblem(null); return; }
    if (typed === null) return;
    const outcome = timeEditOutcome(typed, cleared.current);
    cleared.current = false;
    if (outcome.action === 'restore') { setProblem(null); return; }
    if (outcome.action === 'invalid') { setProblem({ typed, message: outcome.error }); return; }
    setProblem(null);
    const next = outcome.action === 'clear' ? null : outcome.value;
    if ((next || '') !== current) onCommit(next);
  };

  return (
    <input
      id={id}
      type="text"
      className={`${className} plan-time-input${editing === '' && current ? ' plan-time-faded' : ''}${showProblem ? ' plan-time-invalid' : ''}`}
      value={shown}
      // Focused and not yet typed over: the time it holds, faded, as the
      // placeholder — typing replaces it.
      placeholder={editing !== null && current ? current : placeholder}
      maxLength={9}
      disabled={disabled}
      title={showProblem ? `${problem.message}. Not saved — the time is still ${current || 'not set'}.` : title}
      aria-label={ariaLabel}
      aria-invalid={showProblem ? 'true' : undefined}
      onFocus={() => {
        cleared.current = false;
        escaped.current = false;
        // Fixing a time that was refused starts from what was typed.
        setTyped(problem ? problem.typed : '');
      }}
      onChange={(e) => setTyped(e.target.value)}
      onBlur={commit}
      data-time-row={navRow != null ? String(navRow) : undefined}
      data-time-col={navCol != null ? String(navCol) : undefined}
      onKeyDown={(e) => {
        if (timeFieldKeyNav(e)) return;
        if (CLEAR_TIME_KEYS.has(e.key)) cleared.current = true;
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          escaped.current = true;
          e.currentTarget.blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      draggable={false}
    />
  );
}

export default PlanTimeInput;
